/** @jsx jsx */

import { Fragment as _, useRef, useEffect, useMemo, useCallback } from 'react';
import { keyframes } from '@emotion/core';
import { css, jsx } from '@emotion/core';
import { colors } from 'styles/variables';
import getTopics from 'services/apiHelpers/getTopics';
import getTopicSId from 'services/getTopicSId';
import getCurrentLocation from 'services/getCurrentLocation';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import { getButtonFocus } from 'styles/variables';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import CloseBtn from 'components/UI/CloseBtn';
import { useHeader } from 'services/useHeader';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import {
  // Block,
  // Blocks,
  BlockList,
} from 'factories/block';

const { lGrey0 } = colors;

const width = '24px';

const open_menu_kf = keyframes`
from {
	opacity: 0;
	transform: translateX(100%);
}
to {
	opacity: 1;
	transform: translateX(-10px);
}
`;

const close_menu_kf = keyframes`
0% {
	opacity: 1;
	transform: translateX(0%);
}
35% {
	opacity: 1;
	transform: translateX(-10%);
}
100% {
	opacity: 0;
	transform: translateX(100%);
}
`;

interface IMenuProps {
  menu?: boolean;
  color?: string;
  close?: () => void;
}

let topics: BlockList;
let interactionsInTopics;

const getTopicsComplete = (interactionsInTopics, interactionsSeen) => {
  const temp = {};
  Object.keys(interactionsInTopics).forEach((key) => {
    const interactions = interactionsInTopics[key];
    temp[key] = interactions.every((i) => {
      return interactionsSeen.includes(i.id);
    });
  });

  return temp;
};

// const cleanName = name => {
//   return name?.toLowerCase()?.replace(/ /g, '');
// };

const getAnchorId = (a, prefix = 'topic-id-') => {
  if (!a) {
    return '';
  }

  return a?.id?.replace(prefix, '');
};

const Menu = ({ menu, color = lGrey0, close }) => {
  const init = useRef<boolean>(false);
  const nav = useRef<HTMLDivElement | null>(null);
  const tabIndex = useRef<number>(-1);
  const scrollableContainer = useRef<HTMLDivElement>(null);
  const menuItemsContainer = useRef<HTMLUListElement>(null);
  const anchors = useRef<HTMLElement[]>([]);
  const closeButton = useRef<HTMLButtonElement | null>(null);

  const currentTopicId = useSelector((state: State) => {
    return state.currentTopic;
  });

  if (window.dev() || !topics) {
    // console.log('get topics only once in output');
    topics = getTopics();
  }

  if (window.dev() || !interactionsInTopics) {
    // console.log('get interactionsInTopics only once in output');
    //
    interactionsInTopics = topics.reduce((acc, curr, index) => {
      let topicName = curr?.props?.name;
      //
      return {
        ...acc,
        [curr.id]: getInteractionsInTopic(curr),
      };
    }, {});
  }

  const {
    menuBackgroundColor,
    menuTextAndCloseColor,
    menuTextHoverColor,
    menuTextActiveColor,
  } = useSelector((state: State) => {
    const menuColors = (state.theme.menu as unknown as any) || {};

    return {
      menuBackgroundColor: menuColors['Menu background colour'] || '#fff',
      menuTextAndCloseColor: menuColors['Menu text and close colour'] || '#000',
      menuTextHoverColor: menuColors['Menu text hover colour'] || '#0000ff',
      menuTextActiveColor: menuColors['Menu text active colour'] || '#ff0000',
    };
  });

  const header = useHeader();
  //

  const { disableNavigationToIncompleteTopics } =
    header[header.props.headerStyle].props;

  const interactionsSeen = useSelector((state: State) => {
    return state.interactionsSeen;
  });

  const topicsComplete = getTopicsComplete(
    interactionsInTopics,
    interactionsSeen
  );

  useEffect(() => {
    if (closeButton.current && menu) {
      closeButton.current.focus();
    }
  }, [menu]);

  useEffect(() => {
    if (menu) {
      // we set the tabIndex at -1 so the first tab action enters the menu at index 0
      tabIndex.current = -1;
      window.addEventListener('keydown', keyOverrides);
    }

    function keyOverrides(e) {
      switch (e.key) {
        case 'Escape': {
          return close();
        }

        case 'Tab': {
          e.preventDefault();

          if (nav.current && !anchors.current.length) {
            //
            //
            anchors.current = [
              ...Array.from(nav.current.querySelectorAll('a'))
                .filter((a, index) => {
                  //
                  if (!disableNavigationToIncompleteTopics) {
                    return true;
                  }

                  return topicsComplete[getAnchorId(a)];
                })
                .filter((a, index) => {
                  return getAnchorId(a) !== currentTopicId;
                }),
            ];
          }

          if (e.shiftKey) {
            if (tabIndex.current > 0) {
              tabIndex.current = tabIndex.current - 1;
            }
          } else {
            //
            if (tabIndex.current < anchors.current.length - 1) {
              tabIndex.current = tabIndex.current + 1;
            } else {
              // loop back around to the close btn

              tabIndex.current = 0;
            }
          }

          if (
            closeButton.current &&
            !anchors.current.includes(closeButton.current)
          ) {
            // once we tab into the actual anchors / menu items list
            // we add the close btn to that list
            // then we can nav to it with the usual tab flow
            //

            anchors.current = [closeButton.current, ...anchors.current];
            tabIndex.current = 1;
            //
          }

          anchors.current[tabIndex.current]?.focus();
        }
      }
    }

    return () => {
      // clear the anchors out so we can start fresh on next menu open
      anchors.current = [];
      window.removeEventListener('keydown', keyOverrides);
    };
  }, [menu]);

  useEffect(() => {
    if (!nav.current) {
      return;
    }

    const onAnimationEnd = (e: any) => {
      const { target } = e;
      if (target == nav.current) {
        // why wouldn't it be ???
        if (!menu && init.current) {
          requestAnimationFrame(() => {
            if (nav.current) {
              nav.current.style.visibility = 'hidden';
            }
          });
        }
      }
    };

    nav.current.addEventListener('animationend', onAnimationEnd);

    return () => {
      nav.current &&
        nav.current.removeEventListener('animationend', onAnimationEnd);
    };
  }, [menu]);

  useEffect(() => {
    if (menu && !init.current) {
      init.current = true;
    }
  }, [menu]);

  useEffect(() => {
    //
    // this is so we can that nicer bit of padding around the content
    // when scrolling kicks in
    // otherwise it butts right up against the scrollBar
    if (scrollableContainer.current && menuItemsContainer.current) {
      const { height } = menuItemsContainer.current.getBoundingClientRect();
      const maxHeight: number = window.innerHeight - 175;

      scrollableContainer.current.style.padding = `0 ${
        height > maxHeight ? 10 : 0
      }px 0 0`;
    }
  }, [menu]);

  let closeBtnFocus: any = getButtonFocus(menuTextAndCloseColor);

  closeBtnFocus = closeBtnFocus?.split(',')[1]?.trim();

  return (
    <nav
      ref={nav}
      aria-hidden={!menu}
      aria-label="navigation"
      css={css`
        position: absolute;
        right: 30px;
        top: 10px;
        min-width: 300px;
        padding: 65px 20px 20px 20px;
        background: ${menuBackgroundColor};
        ${!menu &&
        !init.current &&
        `
          visibility: hidden;`}

        animation: ${menu ? open_menu_kf : close_menu_kf} 0.3s forwards;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

        @media (max-width: 480px) {
          right: 25px;
          top: 5px;
          width: calc(100vw - 10px);
          padding: 45px 10px 20px 15px;
        }
      `}
      style={{
        ...(menu &&
          init.current && {
            visibility: 'visible',
          }),
      }}
      tabIndex={-1}
    >
      <button
        ref={closeButton}
        className="fader_1s"
        css={css({
          outline: 'none',
          border: 'none',
          position: 'absolute',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          right: 20,
          top: 13,
          width,
          height: width,
          background: 'transparent',
          ':focus': {
            boxShadow: `0 0 0 2px ${menuBackgroundColor}, ${closeBtnFocus}`,
          },

          '@media (max-width: 480px)': {
            right: 10,
            top: 8,
          },
        })}
        onClick={() => {
          close();
        }}
        aria-label="close menu"
      >
        <CloseBtn
          color={menuTextAndCloseColor}
          style={{ width, height: width }}
        />
      </button>

      <div
        ref={scrollableContainer}
        css={css({
          maxHeight: 'calc(100vh - 175px)',
          overflowY: 'auto',
        })}
      >
        <ul ref={menuItemsContainer}>
          {topics
            .filter((t: any) => {
              return !t.props.hiddenFromMenu;
            })
            .map((t: any, index: number, arr: any) => {
              const { id } = t;
              const { name } = t.props;
              const isCurrentTopic = id === currentTopicId;

              let disableNav = '';
              if (disableNavigationToIncompleteTopics) {
                if (!topicsComplete[id]) {
                  disableNav = `
                  color: ${menuTextAndCloseColor};
                  opacity: 0.5;
                  pointer-events: none;
                  `;
                }
              }

              return (
                <li
                  key={id}
                  css={css`
                    margin: 5px 0;
                    text-align: right;
                    cursor: pointer;
                    list-style: none;
                    // font-weight: ${isCurrentTopic ? 700 : 'normal'};

                    line-height: ${1.2 * 1.5}em;
                    pointer-events: ${isCurrentTopic ? 'none' : 'auto'};

                    a:link {
                      color: ${isCurrentTopic
                        ? menuTextActiveColor
                        : menuTextAndCloseColor};
                    }

                    a:hover {
                      color: ${menuTextHoverColor};
                    }

                    a:focus {
                      opacity: 1 !important;
                      color: ${menuTextHoverColor};
                    }

                    ${disableNav}
                  `}
                >
                  <a
                    id={`topic-id-${id}`}
                    href={!disableNav ? `#${id}` : undefined}
                    onClick={(e) => {
                      e.preventDefault();

                      const topic = topics.find((t: any) => {
                        return t.id === id;
                      });

                      const targetLocation = getTopicSId(topic);

                      if (!targetLocation) {
                        return;
                      }

                      (window as any).locationHistory = getCurrentLocation();
                      //

                      window.location.hash = targetLocation;
                    }}
                    onKeyUp={(e) => {
                      if (e.key === ' ') {
                        e.currentTarget.click();
                      }
                    }}
                    {...(id === getCurrentTopic()?.id
                      ? { 'aria-current': 'page' }
                      : {})}
                    role={disableNav ? 'link' : undefined}
                    aria-disabled={disableNav ? true : undefined}
                  >
                    {name || id}
                  </a>
                </li>
              );
            })}
        </ul>
      </div>
    </nav>
  );
};

export { Menu };
